import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { Gem, ShieldCheck, Handshake, Crown, Compass } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { TRUST_ITEMS, BRAND_FULL } from "@/config/site";
import { openBooking } from "@/lib/api";

const ICONS = { Gem, ShieldCheck, Handshake, Crown, Compass };

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
    const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "60%"]);
    const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

    return (
        <section ref={ref} className="relative h-[100svh] min-h-[620px] overflow-hidden grain" data-testid="hero-section">
            <motion.div className="absolute inset-0" style={{ y: imgY }}>
                <div className="hero-kenburns w-full h-[115%]">
                    <img
                        src="/images/hero-new.jpg"
                        alt="Heritage gold and polki bridal necklace set at BSK Jewellers, Rani Ka Bagh, Amritsar"
                        className="w-full h-full object-cover"
                        fetchPriority="high"
                    />
                </div>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/55 to-obsidian/20" />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-transparent to-obsidian/40" />

            <motion.div
                className="absolute w-px bg-gradient-to-b from-transparent via-gold to-transparent left-6 sm:left-12 top-0 bottom-0"
                initial={{ scaleY: 0 }}
                animate={{ scaleY: 1 }}
                transition={{ duration: 1.6, delay: 0.6, ease: "easeInOut" }}
                style={{ transformOrigin: "top" }}
            />

            <motion.div
                className="relative h-full max-w-[1500px] mx-auto px-6 sm:px-16 flex flex-col justify-end pb-28 sm:pb-32"
                style={{ y: contentY, opacity: contentOpacity }}
            >
                <motion.p
                    className="eyebrow mb-6"
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.7 }}
                    data-testid="hero-eyebrow"
                >
                    {BRAND_FULL} · Rani Ka Bagh, Amritsar
                </motion.p>
                <h1 className="font-serif text-4xl sm:text-6xl lg:text-[5.2rem] leading-[1.04] tracking-tight max-w-4xl" data-testid="hero-headline">
                    <MaskedLine delay={0.85}>A Legacy of <span className="italic gold-text">Elegance,</span></MaskedLine>
                    <MaskedLine delay={1.0}>Crafted for You.</MaskedLine>
                </h1>
                <motion.p
                    className="text-sand text-sm sm:text-base max-w-md mt-6 leading-relaxed"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.35 }}
                    data-testid="hero-subcopy"
                >
                    Discover timeless jewellery created to celebrate life's most precious moments.
                </motion.p>
                <motion.div
                    className="flex flex-wrap items-center gap-4 sm:gap-6 mt-10"
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 1.55 }}
                >
                    <Link
                        to="/collections"
                        data-testid="hero-explore-collections-btn"
                        className="bg-gold text-obsidian text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:bg-gold-bright transition-colors duration-300"
                    >
                        Explore Collections
                    </Link>
                    <Link
                        to="/visit"
                        data-testid="hero-visit-showroom-btn"
                        className="border border-ivory/40 text-ivory text-[11px] tracking-[0.25em] uppercase px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
                    >
                        Visit Our Showroom
                    </Link>
                    <button
                        onClick={() => openBooking()}
                        data-testid="hero-book-consultation-link"
                        className="text-[11px] tracking-[0.22em] uppercase text-sand hover:text-gold transition-colors duration-300 underline-offset-8 hover:underline"
                    >
                        Book a Private Consultation →
                    </button>
                </motion.div>
            </motion.div>

            <motion.div
                className="absolute bottom-8 right-8 sm:right-12 hidden sm:flex flex-col items-center gap-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.2, duration: 1 }}
            >
                <span className="text-[9px] tracking-[0.35em] uppercase text-sand" style={{ writingMode: "vertical-rl" }}>Scroll</span>
                <motion.div
                    className="w-px h-12 bg-gold/50"
                    animate={{ scaleY: [0, 1, 1], opacity: [0, 1, 0.3] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                    style={{ transformOrigin: "top" }}
                />
            </motion.div>
        </section>
    );
};

export const TrustStrip = () => (
    <section className="border-y border-gold/15 bg-charcoal/40" data-testid="trust-strip">
        <div className="max-w-[1500px] mx-auto px-5 sm:px-8 grid grid-cols-2 md:grid-cols-5">
            {TRUST_ITEMS.map((t, i) => {
                const Icon = ICONS[t.icon];
                return (
                    <motion.div
                        key={t.title}
                        initial={{ opacity: 0, y: 14 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: i * 0.08 }}
                        className={`flex flex-col items-center text-center gap-2.5 py-8 px-3 ${i > 0 ? "md:border-l border-gold/10" : ""}`}
                        data-testid={`trust-${t.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    >
                        <Icon size={20} strokeWidth={1.25} className="text-gold" />
                        <p className="text-[11px] tracking-[0.22em] uppercase text-ivory">{t.title}</p>
                        <p className="text-[11px] text-sand leading-snug">{t.note}</p>
                    </motion.div>
                );
            })}
        </div>
    </section>
);

export default Hero;
